import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Radio, LogOut, Home as HomeIcon, Mic2, Video, Image as ImageIcon,
  Inbox, Users, Plus, Trash2, Check, X, Upload, Loader2, Wand2, FileText, Download, BookOpen, Youtube, MonitorPlay, UserPlus, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api, media, img, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PILLARS, pillarMeta } from "../lib/constants";

const TABS = [
  { id: "studio", label: "Video Studio", icon: Wand2 },
  { id: "episodes", label: "Episodes", icon: Mic2 },
  { id: "videos", label: "Videos", icon: Video },
  { id: "photos", label: "Photos", icon: ImageIcon },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "guests", label: "Guest Requests", icon: UserPlus },
  { id: "subscribers", label: "Subscribers", icon: Users },
  { id: "guide", label: "Broadcast Guide", icon: BookOpen },
];

const label = "font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8] block mb-1.5";
const input = "w-full rounded-lg bg-[#050B14] border border-[#0066FF]/25 px-3 py-2.5 text-sm outline-none focus:border-[#0066FF]";

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("studio");
  const [stats, setStats] = useState({});

  const loadStats = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => { if (user) loadStats(); }, [user, loadStats]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#050B14] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#0066FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050B14] text-[#F5F7FA]" data-testid="admin-page">
      {/* Top bar */}
      <header className="border-b border-[#0066FF]/20 bg-[#0C1424] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Radio className="h-6 w-6 text-[#0066FF]" strokeWidth={2.5} />
            <span className="font-display text-lg tracking-wide">ADMIN CMS</span>
            <span className="font-mono2 text-[10px] text-[#FF6B00] uppercase hidden sm:inline">The Other Side of Blue</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/studio" className="flex items-center gap-1.5 text-[#FF6B00] hover:text-[#FF8526] font-cond uppercase text-sm font-bold" data-testid="admin-studio-link">
              <Radio className="h-4 w-4" /> <span className="hidden sm:inline">Live Studio</span>
            </Link>
            <Link to="/" className="flex items-center gap-1.5 text-[#94A3B8] hover:text-[#0066FF] font-cond uppercase text-sm" data-testid="admin-view-site">
              <HomeIcon className="h-4 w-4" /> <span className="hidden sm:inline">View Site</span>
            </Link>
            <button
              onClick={() => { logout(); navigate("/login"); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-[#94A3B8] hover:text-[#FF3B30] font-cond uppercase text-sm"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
          {[
            ["Episodes", stats.episodes], ["Videos", stats.videos], ["Photos", stats.photos],
            ["Submissions", stats.submissions], ["Pending", stats.pending], ["Subscribers", stats.subscribers], ["Guests", stats.guests],
          ].map(([k, v]) => (
            <div key={k} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-4">
              <div className="font-display text-3xl text-[#0066FF]">{v ?? 0}</div>
              <div className="font-mono2 text-[10px] uppercase tracking-widest text-[#94A3B8]">{k}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#0066FF]/15 pb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className="flex items-center gap-2 rounded-full px-4 py-2 font-cond font-bold uppercase text-sm tracking-wide transition-colors"
                style={
                  tab === t.id
                    ? { background: "#0066FF", color: "#fff" }
                    : { background: "#121D33", color: "#94A3B8" }
                }
              >
                <Icon className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "studio" && <StudioTab onChange={loadStats} />}
        {tab === "episodes" && <EpisodesTab onChange={loadStats} />}
        {tab === "videos" && <VideosTab onChange={loadStats} />}
        {tab === "photos" && <PhotosTab onChange={loadStats} />}
        {tab === "submissions" && <SubmissionsTab onChange={loadStats} />}
        {tab === "guests" && <GuestRequestsTab onChange={loadStats} />}
        {tab === "subscribers" && <SubscribersTab />}
        {tab === "guide" && <GuideTab />}
      </div>
    </div>
  );
}

function FileField({ label: lbl, accept, onChange, value, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-[#0066FF]/35 px-3 py-2.5 text-sm text-[#94A3B8] hover:border-[#0066FF] transition-colors" data-testid={testid}>
      <Upload className="h-4 w-4 shrink-0" />
      <span className="truncate">{value ? value.name : lbl}</span>
      <input type="file" accept={accept} className="hidden" onChange={(e) => onChange(e.target.files?.[0] || null)} />
    </label>
  );
}

/* ---------------- Episodes ---------------- */
function EpisodesTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", pillar: "citizen_voices", duration: "", guest: "" });
  const [audio, setAudio] = useState(null);
  const [cover, setCover] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/episodes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (audio) fd.append("audio", audio);
      if (cover) fd.append("cover", cover);
      await api.post("/admin/episodes", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Episode published");
      setForm({ title: "", description: "", pillar: "citizen_voices", duration: "", guest: "" });
      setAudio(null); setCover(null);
      load(); onChange();
    } catch { toast.error("Upload failed"); } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/admin/episodes/${id}`);
    toast.success("Deleted"); load(); onChange();
  };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 h-fit" data-testid="admin-episode-form">
        <h3 className="font-cond font-bold uppercase text-lg mb-4 flex items-center gap-2"><Plus className="h-5 w-5 text-[#0066FF]" /> New Episode</h3>
        <div className="space-y-3">
          <div><label className={label}>Title</label><input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-episode-title" /></div>
          <div><label className={label}>Guest</label><input className={input} value={form.guest} onChange={(e) => setForm({ ...form, guest: e.target.value })} /></div>
          <div><label className={label}>Description</label><textarea rows={3} className={`${input} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Pillar</label><select className={input} value={form.pillar} onChange={(e) => setForm({ ...form, pillar: e.target.value })}>{PILLARS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
            <div><label className={label}>Duration</label><input className={input} placeholder="42:10" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} /></div>
          </div>
          <div><label className={label}>Audio File</label><FileField label="Upload audio (mp3, wav...)" accept="audio/*" value={audio} onChange={setAudio} testid="admin-episode-audio" /></div>
          <div><label className={label}>Cover Image</label><FileField label="Upload cover image" accept="image/*" value={cover} onChange={setCover} testid="admin-episode-cover" /></div>
          <button type="submit" disabled={saving} data-testid="admin-create-episode-submit-button" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {saving ? "Publishing..." : "Publish Episode"}
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 space-y-3" data-testid="admin-episodes-list">
        {items.map((ep) => {
          const meta = pillarMeta(ep.pillar);
          return (
            <div key={ep.id} className="flex items-center gap-4 rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-3">
              <img src={img(ep.cover_file_id, ep.cover_url)} alt="" className="h-14 w-14 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="font-cond font-bold uppercase truncate">{ep.title}</div>
                <div className="text-xs font-mono2" style={{ color: meta.color }}>{meta.short} · {ep.duration}</div>
              </div>
              <button onClick={() => del(ep.id)} data-testid={`admin-delete-episode-${ep.id}`} className="text-[#94A3B8] hover:text-[#FF3B30] p-2"><Trash2 className="h-5 w-5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Videos ---------------- */
function VideosTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "bridging_gap" });
  const [video, setVideo] = useState(null);
  const [thumb, setThumb] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/videos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (video) fd.append("video", video);
      if (thumb) fd.append("thumbnail", thumb);
      await api.post("/admin/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video added");
      setForm({ title: "", description: "", category: "bridging_gap" }); setVideo(null); setThumb(null);
      load(); onChange();
    } catch { toast.error("Upload failed"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/admin/videos/${id}`); toast.success("Deleted"); load(); onChange(); };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 h-fit" data-testid="admin-video-form">
        <h3 className="font-cond font-bold uppercase text-lg mb-4 flex items-center gap-2"><Plus className="h-5 w-5 text-[#0066FF]" /> New Video</h3>
        <div className="space-y-3">
          <div><label className={label}>Title</label><input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-video-title" /></div>
          <div><label className={label}>Description</label><textarea rows={3} className={`${input} resize-none`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div><label className={label}>Category</label><select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{PILLARS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          <div><label className={label}>Video File</label><FileField label="Upload video (mp4, mov...)" accept="video/*" value={video} onChange={setVideo} testid="admin-video-file" /></div>
          <div><label className={label}>Thumbnail</label><FileField label="Upload thumbnail image" accept="image/*" value={thumb} onChange={setThumb} testid="admin-video-thumb" /></div>
          <button type="submit" disabled={saving} data-testid="admin-create-video-submit-button" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {saving ? "Uploading..." : "Add Video"}
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 grid sm:grid-cols-2 gap-4 content-start" data-testid="admin-videos-list">
        {items.map((v) => (
          <div key={v.id} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] overflow-hidden">
            <div className="relative aspect-video">
              <img src={img(v.thumbnail_file_id, v.thumbnail_url)} alt="" className="w-full h-full object-cover" />
              <button onClick={() => del(v.id)} data-testid={`admin-delete-video-${v.id}`} className="absolute top-2 right-2 rounded-full bg-[#050B14]/80 p-2 text-[#94A3B8] hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="p-3"><div className="font-cond font-bold uppercase text-sm truncate">{v.title}</div></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Photos ---------------- */
function PhotosTab({ onChange }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "community" });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/photos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!image) { toast.error("Please choose an image"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("image", image);
      await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      setForm({ title: "", category: "community" }); setImage(null);
      load(); onChange();
    } catch { toast.error("Upload failed"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/admin/photos/${id}`); toast.success("Deleted"); load(); onChange(); };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 h-fit" data-testid="admin-photo-form">
        <h3 className="font-cond font-bold uppercase text-lg mb-4 flex items-center gap-2"><Plus className="h-5 w-5 text-[#0066FF]" /> New Photo</h3>
        <div className="space-y-3">
          <div><label className={label}>Title</label><input className={input} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-photo-title" /></div>
          <div><label className={label}>Category</label><select className={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="st_louis">St. Louis</option><option value="community">Community</option><option value="studio">Studio</option>
          </select></div>
          <div><label className={label}>Image</label><FileField label="Upload image" accept="image/*" value={image} onChange={setImage} testid="admin-photo-file" /></div>
          <button type="submit" disabled={saving} data-testid="admin-create-photo-submit-button" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#0066FF] hover:bg-[#2B82FF] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {saving ? "Uploading..." : "Add Photo"}
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 columns-2 sm:columns-3 gap-3 space-y-3" data-testid="admin-photos-list">
        {items.map((p) => (
          <div key={p.id} className="relative break-inside-avoid rounded-xl overflow-hidden border border-[#0066FF]/15">
            <img src={img(p.image_file_id, p.image_url)} alt="" className="w-full object-cover" />
            <button onClick={() => del(p.id)} data-testid={`admin-delete-photo-${p.id}`} className="absolute top-2 right-2 rounded-full bg-[#050B14]/80 p-2 text-[#94A3B8] hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Submissions ---------------- */
function SubmissionsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/submissions").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/admin/submissions/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success(status === "approved" ? "Approved & published" : "Rejected");
    load(); onChange();
  };
  const del = async (id) => { await api.delete(`/admin/submissions/${id}`); toast.success("Deleted"); load(); onChange(); };

  return (
    <div className="space-y-3" data-testid="admin-submissions-list">
      {items.length === 0 && <div className="text-[#94A3B8] font-mono2 uppercase text-sm py-10 text-center">No submissions yet</div>}
      {items.map((s) => {
        const meta = pillarMeta(s.category);
        return (
          <div key={s.id} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-5" data-testid={`admin-submission-${s.id}`}>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <span className="font-cond font-bold uppercase">{s.name}</span>
                <span className="ml-3 font-mono2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.short}</span>
              </div>
              <span className="font-mono2 text-[10px] uppercase px-2 py-0.5 rounded-full" style={{
                background: s.status === "approved" ? "#10B98122" : s.status === "rejected" ? "#FF3B3022" : "#FF6B0022",
                color: s.status === "approved" ? "#10B981" : s.status === "rejected" ? "#FF3B30" : "#FF6B00",
              }}>{s.status}</span>
            </div>
            <p className="text-sm text-[#CBD5E1] mb-3">{s.story}</p>
            {s.audio_file_id && <audio src={media(s.audio_file_id, null)} controls className="w-full mb-3 h-9" />}
            {s.photo_file_id && <img src={media(s.photo_file_id, null)} alt="" className="max-h-40 rounded-lg mb-3" />}
            <div className="flex gap-2">
              <button onClick={() => setStatus(s.id, "approved")} data-testid={`admin-approve-${s.id}`} className="flex items-center gap-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:bg-[#10B981]/25"><Check className="h-4 w-4" /> Approve</button>
              <button onClick={() => setStatus(s.id, "rejected")} data-testid={`admin-reject-${s.id}`} className="flex items-center gap-1.5 rounded-lg bg-[#FF6B00]/15 text-[#FF6B00] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:bg-[#FF6B00]/25"><X className="h-4 w-4" /> Reject</button>
              <button onClick={() => del(s.id)} data-testid={`admin-delete-submission-${s.id}`} className="flex items-center gap-1.5 rounded-lg text-[#94A3B8] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /> Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------------- Subscribers ---------------- */
function SubscribersTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/subscribers").then((r) => setItems(r.data)); }, []);
  return (
    <div className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] overflow-hidden" data-testid="admin-subscribers-list">
      {items.length === 0 && <div className="text-[#94A3B8] font-mono2 uppercase text-sm py-10 text-center">No subscribers yet</div>}
      {items.map((s, i) => (
        <div key={s.id} className={`flex items-center justify-between px-5 py-3 ${i % 2 ? "bg-[#0C1424]" : ""}`}>
          <span className="text-sm">{s.email}</span>
          <span className="font-mono2 text-xs text-[#94A3B8]">{new Date(s.created_at).toLocaleDateString()}</span>
        </div>
      ))}
    </div>
  );
}


/* ---------------- Video Studio (PDF/Text -> narrated video) ---------------- */
const VOICES = [
  ["onyx", "Onyx — deep, authoritative"],
  ["echo", "Echo — smooth, calm"],
  ["fable", "Fable — expressive, storytelling"],
  ["nova", "Nova — energetic, upbeat"],
  ["sage", "Sage — wise, measured"],
  ["shimmer", "Shimmer — bright"],
  ["alloy", "Alloy — neutral"],
  ["ash", "Ash — clear"],
  ["coral", "Coral — warm"],
];

const STATUS_LABEL = {
  queued: "Queued", extracting: "Preparing text", narrating: "Generating narration",
  rendering: "Rendering video", uploading: "Saving video", done: "Done", error: "Error",
};

function StudioTab({ onChange }) {
  const [jobs, setJobs] = useState([]);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [pdf, setPdf] = useState(null);
  const [images, setImages] = useState([]);
  const [voice, setVoice] = useState("onyx");
  const [model, setModel] = useState("tts-1-hd");
  const [category, setCategory] = useState("citizen_voices");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/video-studio").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Poll while any job is in progress
  useEffect(() => {
    const active = jobs.some((j) => !["done", "error"].includes(j.status));
    if (!active) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [jobs]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error("Add a title"); return; }
    if (!pdf && !text.trim()) { toast.error("Add a PDF or paste some text"); return; }
    if (images.length === 0) { toast.error("Upload at least one background image"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("text", text);
      fd.append("voice", voice);
      fd.append("model", model);
      fd.append("category", category);
      if (pdf) fd.append("pdf", pdf);
      images.forEach((im) => fd.append("images", im));
      await api.post("/admin/video-studio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video is generating — watch its progress below");
      setTitle(""); setText(""); setPdf(null); setImages([]);
      load(); onChange();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start the job");
    } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/admin/video-studio/${id}`); toast.success("Removed"); load(); };

  return (
    <div className="grid lg:grid-cols-5 gap-8">
      <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6 h-fit" data-testid="admin-studio-form">
        <h3 className="font-cond font-bold uppercase text-lg mb-1 flex items-center gap-2"><Wand2 className="h-5 w-5 text-[#FF6B00]" /> PDF / Text → Video</h3>
        <p className="text-xs text-[#94A3B8] mb-4">Narrated with AI voice over a Ken Burns slideshow of your images + live waveform. Auto-publishes to Videos.</p>
        <div className="space-y-3">
          <div><label className={label}>Title</label><input className={input} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="studio-title-input" /></div>
          <div>
            <label className={label}>PDF (optional)</label>
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-[#0066FF]/35 px-3 py-2.5 text-sm text-[#94A3B8] hover:border-[#0066FF]" data-testid="studio-pdf-upload">
              <FileText className="h-4 w-4 shrink-0" /><span className="truncate">{pdf ? pdf.name : "Upload a PDF to narrate"}</span>
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => setPdf(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div><label className={label}>…or paste text</label><textarea rows={4} className={`${input} resize-none`} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste a script or article here" data-testid="studio-text-input" /></div>
          <div>
            <label className={label}>Background images (1+)</label>
            <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-dashed border-[#0066FF]/35 px-3 py-2.5 text-sm text-[#94A3B8] hover:border-[#0066FF]" data-testid="studio-images-upload">
              <Upload className="h-4 w-4 shrink-0" /><span className="truncate">{images.length ? `${images.length} image(s) selected` : "Upload photos (faces, Arch, community)"}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setImages(Array.from(e.target.files || []))} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={label}>Voice</label><select className={input} value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="studio-voice-select">{VOICES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></div>
            <div><label className={label}>Quality</label><select className={input} value={model} onChange={(e) => setModel(e.target.value)} data-testid="studio-model-select"><option value="tts-1-hd">HD (best)</option><option value="tts-1">Standard (faster)</option></select></div>
          </div>
          <div><label className={label}>Category</label><select className={input} value={category} onChange={(e) => setCategory(e.target.value)}>{PILLARS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          <button type="submit" disabled={saving} data-testid="studio-generate-button" className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8526] disabled:opacity-60 transition-colors px-5 py-3 font-cond font-bold uppercase text-[#050B14]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {saving ? "Starting..." : "Generate Video"}
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 space-y-4" data-testid="admin-studio-jobs">
        {jobs.length === 0 && <div className="text-[#94A3B8] font-mono2 uppercase text-sm py-10 text-center">No videos generated yet</div>}
        {jobs.map((j) => {
          const busy = !["done", "error"].includes(j.status);
          return (
            <div key={j.id} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-5" data-testid={`studio-job-${j.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="font-cond font-bold uppercase">{j.title}</div>
                <button onClick={() => del(j.id)} className="text-[#94A3B8] hover:text-[#FF3B30]" data-testid={`studio-delete-${j.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                {busy && <Loader2 className="h-4 w-4 text-[#FF6B00] animate-spin" />}
                <span className="font-mono2 text-xs uppercase" style={{ color: j.status === "error" ? "#FF3B30" : j.status === "done" ? "#10B981" : "#FF6B00" }}>
                  {STATUS_LABEL[j.status] || j.status}
                </span>
                <span className="text-xs text-[#94A3B8]">{j.message}</span>
              </div>
              {busy && (
                <div className="h-2 rounded-full bg-[#050B14] overflow-hidden mb-1">
                  <div className="h-full bg-[#FF6B00] transition-all duration-500" style={{ width: `${j.progress || 0}%` }} />
                </div>
              )}
              {j.status === "done" && j.video_file_id && (
                <div className="mt-3">
                  <video src={fileUrl(j.video_file_id)} controls className="w-full rounded-lg border border-[#0066FF]/20 bg-black" data-testid={`studio-video-${j.id}`} />
                  <a href={fileUrl(j.video_file_id)} download={`${j.title}.mp4`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm text-[#0066FF] hover:text-[#2B82FF] font-cond uppercase" data-testid={`studio-download-${j.id}`}>
                    <Download className="h-4 w-4" /> Download MP4
                  </a>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- Broadcast Guide ---------------- */
function Step({ n, children }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#0066FF]/15 border border-[#0066FF]/40 font-mono2 text-xs text-[#0066FF]">{n}</span>
      <span className="text-sm text-[#CBD5E1] leading-relaxed">{children}</span>
    </li>
  );
}

function GuideTab() {
  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-guide-tab">
      <div className="rounded-2xl border border-[#0066FF]/25 bg-[#0C1424] p-6">
        <h2 className="font-display text-3xl uppercase leading-none mb-2">Go-Live Guide</h2>
        <p className="text-sm text-[#94A3B8]">Compose your show in a broadcast tool, stream to YouTube, then paste the link into Live Studio and press Go Live. Overlays, logo, lower-thirds & split-screen all come from your broadcast tool.</p>
      </div>

      {/* StreamYard */}
      <div className="rounded-2xl border border-[#8B5CF6]/30 bg-[#121D33] p-6">
        <div className="flex items-center gap-2 mb-1">
          <MonitorPlay className="h-5 w-5 text-[#8B5CF6]" />
          <h3 className="font-cond font-bold uppercase text-xl">Easiest: StreamYard (browser)</h3>
        </div>
        <p className="text-xs text-[#94A3B8] mb-4">Best if you want guests to join by clicking a link — no software to install.</p>
        <ol className="space-y-3">
          <Step n="1">Go to <span className="text-white">streamyard.com</span> and sign up (free tier is fine).</Step>
          <Step n="2">Settings → Destinations → <span className="text-white">Connect</span> your YouTube (@dennishaymon4027) and allow access.</Step>
          <Step n="3">Click <span className="text-white">Create a broadcast</span> → pick your YouTube destination → add a title → Create.</Step>
          <Step n="4">Left panel → <span className="text-white">Brand</span>: upload your Blue logo. <span className="text-white">Banners</span>: type guest names/titles (your lower-thirds).</Step>
          <Step n="5">Click <span className="text-white">Invite</span> → copy the guest link → text/email it. Your guest clicks, allows camera/mic, and joins backstage.</Step>
          <Step n="6">Put you + guest on screen (click each "+"), choose the <span className="text-white">split-screen</span> layout, add a name banner.</Step>
          <Step n="7">Click <span className="text-white">Go Live</span> (top-right).</Step>
          <Step n="8">Copy the YouTube <span className="text-white">watch link</span> (looks like youtube.com/watch?v=XXXXXXXXXXX).</Step>
          <Step n="9">Here in Admin → <span className="text-[#FF6B00]">Live Studio</span> → paste the link + episode title → <span className="text-[#FF6B00]">Go Live on Site</span>.</Step>
          <Step n="10">When finished: End broadcast in StreamYard, then <span className="text-white">Stop / Set Offline</span> on your site.</Step>
        </ol>
      </div>

      {/* OBS */}
      <div className="rounded-2xl border border-[#0066FF]/25 bg-[#121D33] p-6">
        <div className="flex items-center gap-2 mb-1">
          <MonitorPlay className="h-5 w-5 text-[#0066FF]" />
          <h3 className="font-cond font-bold uppercase text-xl">Pro: OBS Studio (free)</h3>
        </div>
        <p className="text-xs text-[#94A3B8] mb-4">Best if you want full control over scenes and graphics.</p>
        <ol className="space-y-3">
          <Step n="1">Download <span className="text-white">OBS Studio</span> from obsproject.com (free).</Step>
          <Step n="2">In YouTube: <span className="text-white">Create → Go Live → Stream</span> → copy your Stream Key.</Step>
          <Step n="3">OBS → <span className="text-white">Settings → Stream</span> → Service = YouTube → paste the Stream Key → OK.</Step>
          <Step n="4">Build a Scene with Sources: your camera (Video Capture Device, left), guest video (Window Capture of a Zoom/Meet call, right), your logo (Image), and names (Text/GDI+ lower-thirds).</Step>
          <Step n="5">Arrange the two cameras side-by-side for a split-screen.</Step>
          <Step n="6">Click <span className="text-white">Start Streaming</span> in OBS — your stream appears on YouTube.</Step>
          <Step n="7">Copy the YouTube watch link → paste into Live Studio → <span className="text-[#FF6B00]">Go Live on Site</span>.</Step>
          <Step n="8">Finish: Stop Streaming in OBS, then Stop / Set Offline on your site.</Step>
        </ol>
      </div>

      <div className="rounded-2xl border border-[#FF6B00]/30 bg-[#0C1424] p-6">
        <div className="flex items-center gap-2 mb-2">
          <Youtube className="h-5 w-5 text-[#FF0000]" />
          <h3 className="font-cond font-bold uppercase text-lg">30-Second Recap</h3>
        </div>
        <p className="text-sm text-[#CBD5E1] leading-relaxed">
          Compose in OBS/StreamYard → Go Live to YouTube → copy the YouTube link → paste it in
          <span className="text-[#FF6B00]"> Live Studio</span> → press <span className="text-[#FF6B00]">Go Live on Site</span>.
          Viewers watch and chat right on the site.
        </p>
        <p className="mt-3 text-xs text-[#94A3B8]">
          <span className="text-white font-semibold">Tip:</span> Do a private test first — set the YouTube stream to <span className="text-white">Unlisted</span>, run through it end-to-end, then switch to <span className="text-white">Public</span> for the real show.
        </p>
      </div>
    </div>
  );
}


/* ---------------- Guest Requests ---------------- */
function GuestRequestsTab({ onChange }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/guest-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    const fd = new FormData(); fd.append("status", status);
    await api.patch(`/admin/guest-requests/${id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Updated"); load(); onChange && onChange();
  };
  const del = async (id) => { await api.delete(`/admin/guest-requests/${id}`); toast.success("Deleted"); load(); onChange && onChange(); };

  return (
    <div className="space-y-3" data-testid="admin-guest-requests-list">
      {items.length === 0 && <div className="text-[#94A3B8] font-mono2 uppercase text-sm py-10 text-center">No guest requests yet</div>}
      {items.map((g) => {
        const meta = pillarMeta(g.topic);
        return (
          <div key={g.id} className="rounded-xl border border-[#0066FF]/15 bg-[#121D33] p-5" data-testid={`guest-request-${g.id}`}>
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <span className="font-cond font-bold uppercase text-lg">{g.name}</span>
                <span className="ml-3 font-mono2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${meta.color}22`, color: meta.color }}>{meta.short}</span>
              </div>
              <span className="font-mono2 text-[10px] uppercase px-2 py-0.5 rounded-full" style={{
                background: g.status === "approved" ? "#10B98122" : g.status === "declined" ? "#FF3B3022" : "#FF6B0022",
                color: g.status === "approved" ? "#10B981" : g.status === "declined" ? "#FF3B30" : "#FF6B00",
              }}>{g.status}</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#94A3B8] mb-3">
              <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {g.email}</span>
              {g.phone && <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {g.phone}</span>}
            </div>
            <p className="text-sm text-[#CBD5E1] mb-4">{g.message}</p>
            <div className="flex gap-2">
              <button onClick={() => setStatus(g.id, "approved")} data-testid={`guest-approve-${g.id}`} className="flex items-center gap-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:bg-[#10B981]/25"><Check className="h-4 w-4" /> Approve</button>
              <button onClick={() => setStatus(g.id, "declined")} data-testid={`guest-decline-${g.id}`} className="flex items-center gap-1.5 rounded-lg bg-[#FF6B00]/15 text-[#FF6B00] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:bg-[#FF6B00]/25"><X className="h-4 w-4" /> Decline</button>
              <button onClick={() => del(g.id)} data-testid={`guest-delete-${g.id}`} className="flex items-center gap-1.5 rounded-lg text-[#94A3B8] px-3 py-1.5 font-cond font-bold uppercase text-xs hover:text-[#FF3B30]"><Trash2 className="h-4 w-4" /> Delete</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

